import { z } from 'zod';

/**
 * Request shapes shared by the Worker and the SPA. One definition means the
 * client cannot send a payload the server does not expect, and the same rules
 * produce the same validation messages on both sides.
 */

export const LOCALES = ['en', 'fr', 'ar'] as const;
export const localeSchema = z.enum(LOCALES);

export const emailSchema = z
  .string()
  .trim()
  .min(3)
  .max(254)
  .toLowerCase()
  .pipe(z.string().email());

/**
 * Length over composition rules. NIST dropped character-class requirements
 * because they push people towards predictable substitutions; a 10-character
 * minimum with a generous ceiling is stronger in practice.
 */
export const passwordSchema = z.string().min(10).max(200);

export const signupSchema = z.object({
  name: z.string().trim().min(1).max(120),
  email: emailSchema,
  password: passwordSchema,
  /** The org created alongside the account — a club, academy or just their name. */
  orgName: z.string().trim().min(1).max(120),
  locale: localeSchema.default('en'),
  timezone: z.string().trim().min(1).max(64).default('UTC'),
});

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1).max(200),
});

export const requestResetSchema = z.object({ email: emailSchema });

export const resetPasswordSchema = z.object({
  token: z.string().min(10).max(200),
  password: passwordSchema,
});

export const verifyEmailSchema = z.object({ token: z.string().min(10).max(200) });

export type SignupInput = z.infer<typeof signupSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

/** What /api/auth/me returns. The SPA's entire notion of "who is signed in". */
export interface SessionUser {
  id: string;
  email: string;
  name: string;
  locale: (typeof LOCALES)[number];
  emailVerified: boolean;
  orgs: Array<{ id: string; name: string; role: string; timezone: string }>;
  activeOrgId: string | null;
  /**
   * Whether this account runs MyPlannerPad itself.
   *
   * Carried so the navigation can show the link. It is never an authorisation:
   * every admin request re-reads the flag from the database, because a session
   * minted before the flag was revoked must not keep the keys.
   */
  isPlatformAdmin?: boolean;
}
